// FILTRES DE L'ANNUAIRE — partagés par l'onglet « Annuaire » et par le choix
// des participants d'une invitation : on pioche avec les mêmes critères que
// ceux avec lesquels on range.
import type { Seniority } from '../api/types';
import { SENIORITIES } from '../personas/PersonaNeeds';
import { ENGAGEMENTS, personName, type Engagement, type Person } from './api';
import { Select } from '../components/ui';
import '../studies/studies.css';
import './people.css';

export interface PeopleFilter {
  q: string;
  pool: string;
  /** Le persona incarné (id). */
  persona: string;
  role: string;
  seniority: Seniority | '';
  engagement: Engagement | '';
  keyUser: boolean;
  expert: boolean;
}

export const NO_FILTER: PeopleFilter = { q: '', pool: '', persona: '', role: '', seniority: '', engagement: '', keyUser: false, expert: false };

const fold = (v: string | null | undefined) =>
  String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Les personnes qui passent le filtre. La recherche porte sur le nom,
 *  l'e-mail, la fonction et les notes. */
export function filterPeople(people: Person[], f: PeopleFilter): Person[] {
  const q = fold(f.q.trim());
  return people.filter((p) =>
    (!q || [personName(p), p.email, p.job_title, p.notes].some((v) => fold(v).includes(q)))
    && (!f.pool || p.pool === f.pool)
    && (!f.persona || p.persona_id === f.persona)
    && (!f.role || p.role === f.role)
    && (!f.seniority || p.seniority === f.seniority)
    && (!f.engagement || p.engagement === f.engagement)
    && (!f.keyUser || p.key_user)
    && (!f.expert || p.domain_expert));
}

export const isFiltered = (f: PeopleFilter) =>
  Boolean(f.q.trim() || f.pool || f.persona || f.role || f.seniority || f.engagement || f.keyUser || f.expert);

export function PeopleFilters({
  value: f,
  onChange,
  pools,
  roles,
  people = [],
}: {
  value: PeopleFilter;
  onChange: (f: PeopleFilter) => void;
  pools: string[];
  roles: string[];
  /** Pour proposer les personas incarnés par au moins une personne. */
  people?: Person[];
}) {
  const personas = [...new Map(people.filter((p) => p.persona_id).map((p) => [p.persona_id!, p.persona_name ?? p.persona_id!])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], 'fr'));
  const set = (patch: Partial<PeopleFilter>) => onChange({ ...f, ...patch });
  return (
    <div className="ppl-filters" role="group" aria-label="Filtrer l’annuaire">
      <input
        type="search"
        className="zy-input ppl-search"
        placeholder="Rechercher un nom, une adresse, une fonction…"
        aria-label="Rechercher"
        value={f.q}
        onChange={(e) => set({ q: e.target.value })}
      />
      {pools.length > 0 ? (
        <Select aria-label="Pool" value={f.pool} onChange={(e) => set({ pool: e.target.value })}>
          <option value="">Tous les pools</option>
          {pools.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      ) : null}
      {personas.length > 0 ? (
        <Select aria-label="Persona" value={f.persona} onChange={(e) => set({ persona: e.target.value })}>
          <option value="">Tous les personas</option>
          {personas.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Select>
      ) : null}
      {roles.length > 0 ? (
        <Select aria-label="Rôle" value={f.role} onChange={(e) => set({ role: e.target.value })}>
          <option value="">Tous les rôles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      ) : null}
      <Select
       
        aria-label="Séniorité"
        value={f.seniority}
        onChange={(e) => set({ seniority: e.target.value as Seniority | '' })}
      >
        <option value="">Toute séniorité</option>
        {SENIORITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </Select>
      <Select
       
        aria-label="Engagement"
        value={f.engagement}
        onChange={(e) => set({ engagement: e.target.value as Engagement | '' })}
      >
        <option value="">Tout engagement</option>
        {ENGAGEMENTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
      </Select>
      <button
        type="button"
        className={`st-filter-btn${f.keyUser ? ' st-filter-on' : ''}`}
        aria-pressed={f.keyUser}
        onClick={() => set({ keyUser: !f.keyUser })}
      >
        Key users
      </button>
      <button
        type="button"
        className={`st-filter-btn${f.expert ? ' st-filter-on' : ''}`}
        aria-pressed={f.expert}
        onClick={() => set({ expert: !f.expert })}
      >
        Experts métier
      </button>
      {isFiltered(f) ? (
        <button type="button" className="ppl-reset" onClick={() => onChange(NO_FILTER)}>
          Effacer les filtres
        </button>
      ) : null}
    </div>
  );
}
